import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import PayMultiInvoicesDialogInvoicesSection from "./sections/invoices/index";
import PayMultiInvoicesDialogCreditsSection from "./sections/credits/index";
import {createUUId} from "../../../../core/services/utils/utils";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../../base/button/button";
import TopBarProgressIndicator from "../../app-specific/topbar-progress-indicator";
import PayMultipleInvoicesDialogFinancialSummarySection from "./sections/financial-summary";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import PayMultipleInvoicesDialogSummarySection from "./sections/summary";
import PayCustomerOrder, {PayCustomerOrderSteps} from "../../app-specific/pay-customer-order";
import PayCustomerOrderSendEmailSection from "../../app-specific/pay-customer-order/sections/send-email";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";
import PartnerIndustryRules from "../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../hooks/use-partner-information";
import CacheService from "../../../../core/services/cache/cache_service";


const steps = {
    invoices: 'Invoices',
    credits: 'Credits',
    summary: 'summary',
    payment: 'Payment',
    email: 'email',
};

const processTabs = {
    invoices: steps.invoices,
    credits: steps.credits,
    payment: steps.payment,
}

const PayMultiInvoicesDialog = ({
                                    open,
                                    close,
                                    selectedInvoices: selectedInvoicesProp,
                                    customerId,
                                }) => {
    const [step, setStep] = useState(steps.invoices);
    const [paymentStep, setPaymentStep] = useState(PayCustomerOrderSteps.methodSelection);
    const [selectedCredits, setSelectedCredits] = useState([]);
    const [appliedPayments, setAppliedPayments] = useState([]);
    const [submittingPaymentAction, setSubmittingPaymentAction] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const partnerInformation = usePartnerInformation();

    const paymentRef = useRef();
    const sendEmailFormIdRef = useRef(createUUId(true));
    const sendEmailSubmitterRef = useRef();

    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id],);
    const forceCustomersAsCod = useMemo(() => PartnerIndustryRules.forcesCustomersAsCod(partnerId), [partnerId]);
    const allowApplyCredits = useMemo(() => operatorAccessLevels.packages.sales.customerAvailableCredits && !forceCustomersAsCod, [operatorAccessLevels.packages.sales.customerAvailableCredits, forceCustomersAsCod]);

    const currentProcess = useMemo(() => {
        switch (step) {
            case steps.invoices:
                return processTabs.invoices;
            case steps.credits:
                return processTabs.credits;
            default:
                return processTabs.payment;
        }
    }, [step])

    const visibleProcesses = useMemo(() => {
        if (allowApplyCredits)
            return processTabs;
        return {
            invoices: steps.invoices,
            payment: steps.payment,
        }
    }, [allowApplyCredits])

    const selectedInvoices = useMemo(() => {
        return selectedInvoicesProp?.map(invoice => ({
            ...invoice,
            transactionRecord: {
                id: createUUId(true),
                referenceDisplay: invoice.invoiceNo,
                submittedDate: invoice.invoiceDate,
                transactionNo: invoice.transactionNo.length ? invoice.transactionNo : 'N/A',
                balance: invoice.balance,
                amount: invoice.total,
            }
        })) ?? []
    }, [selectedInvoicesProp])

    const invoiceIds = useMemo(() => selectedInvoices?.map(e => e.id), [selectedInvoices])

    const invoicesAmount = useMemo(() =>
            selectedInvoices
                ?.map(e => PartnerIndustryRules.effectiveRemainingBalance(e, partnerId))
                ?.reduce((t, a) => t + a, 0)
            ?? 0,
        [selectedInvoices, partnerId])

    const creditsAmount = useMemo(() =>
            !allowApplyCredits
                ? 0
                : selectedCredits
                    ?.map(e => Number(e.balance))
                    ?.reduce((t, a) => t + a, 0)
                ?? 0,
        [selectedCredits, allowApplyCredits]);

    const paymentsAmount = useMemo(() =>
            appliedPayments
                ?.map(e => Number(e.amount))
                ?.reduce((t, a) => t + a, 0)
            ?? 0,
        [appliedPayments]);

    const totalAmount = useMemo(() =>
            Number((invoicesAmount + creditsAmount - paymentsAmount).toFixed(2)),
        [invoicesAmount, creditsAmount, paymentsAmount]);

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [open])

    /**
     * with each change in the [open] state of the dialog:
     * - if closed, resets the applied payments, credits and the step.
     */
    useEffect(() => {
        if (open)
            return
        setAppliedPayments([]);
        setSelectedCredits([]);
        setStep(steps.invoices);
        setPaymentStep(operatorAccessLevels.packages.sales.onlinePayments ? PayCustomerOrderSteps.methodSelection : PayCustomerOrderSteps.otherPayments);
    }, [open, operatorAccessLevels.packages.sales.onlinePayments])

    /**
     * Adds a specified credit into the selected credits for paying invoices by calling the api.
     *
     * - If the result of the api is successful, updates the list in state.
     * @type {(function(*): Promise<boolean>)}
     */
    const addCreditToMultipleInvoices = useCallback(async (credit) => {
        const forApi = {
            customerId: customerId,
            transactionNo: credit.transactionNo,
            invoiceIds: invoiceIds,
        };
        const response = await Api.addCreditToMultiInvoices(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSelectedCredits(prevState => [...prevState, credit]);
        }
        return response?.resultFlag;
    }, [customerId, invoiceIds, isMounted])

    /**
     * Removes a specified credit from the selected credits for paying invoices by calling the api.
     *
     * - If the result of the api is successful, updates the list in state.
     * @type {(function(*): Promise<boolean>)}
     */
    const removeCreditFromMultipleInvoices = useCallback(async (credit) => {
        const forApi = {
            customerId: customerId,
            transactionNo: credit.transactionNo,
            invoiceIds: invoiceIds,
        }
        const response = await Api.removeTransactionItemFromMultiInvoices(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSelectedCredits(prevState => prevState?.filter(e => e.transactionNo !== credit.transactionNo));
        }
        return response?.resultFlag;
    }, [customerId, invoiceIds, isMounted])

    /**
     * Pays for the record of invoices of the user by paying the currently remained balance.
     *
     * - If the result of the api is successful, calls the [generateNextOrderEntity] callback to proceed to the next
     * step of this order if applicable.
     * @type {(function(*): Promise<boolean>)}
     */
    const payMultipleInvoices = useCallback(async (values) => {
        let forApi = {
            ...values,
            orderIds: invoiceIds,
            customerId: customerId
        };
        const response = await Api.payMultipleInvoices(forApi);
        if (!isMounted())
            return [];
        if (response?.resultFlag) {
            setAppliedPayments(prevState => [...prevState, response.data]);
        }
        return [
            response?.resultFlag ?? false,
        ]
    }, [customerId, invoiceIds, isMounted])

    /**
     * Sends an email to the provided email address of the user by calling its appropriate api.
     * @type {(function(*=, *=): Promise<boolean>)}
     */
    const sendMultiInvoiceEmail = useCallback(async (emailAddress, includeBCC) => {
        const forApi = {
            customerId: customerId,
            orderId: invoiceIds[0],
            sendBccToOperator: includeBCC,
            email: emailAddress,
        };
        const response = await Api.sendMultiInvoiceInformationToUserEmail(forApi);
        return response?.resultFlag;
    }, [customerId, invoiceIds])

    /**
     * Determines if a ball has been passed over relative to their process of adding multi invoices
     * @type {(function(*, *): boolean)}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case processTabs.invoices:
            default:
                return false
            case processTabs.credits:
                return (type === processTabs.invoices);
            case processTabs.payment:
                return (type === processTabs.credits || type === processTabs.invoices);
        }
    }, [])

    /**@type {JSX.Element}*/
    const financialSummary = useMemo(() => {
        switch (step) {
            case steps.invoices:
            case steps.credits:
            case steps.summary:
                return <PayMultipleInvoicesDialogFinancialSummarySection
                    invoicesAmount={invoicesAmount}
                    creditsAmount={creditsAmount}
                    paymentsAmount={paymentsAmount}
                    totalAmount={totalAmount}
                />
            default:
                return null;
        }
    }, [step, invoicesAmount, creditsAmount, paymentsAmount, totalAmount])

    const dialogSize = useMemo(() => {
        switch (step) {
            case steps.invoices:
            case steps.credits:
            case steps.summary:
            default:
                return 'md';
            case steps.email:
                return 'sm';
            case steps.payment:
                switch (paymentStep) {
                    case PayCustomerOrderSteps.methodSelection:
                    case PayCustomerOrderSteps.onlinePayment:
                    case PayCustomerOrderSteps.otherPayments:
                        return 'sm'
                    case PayCustomerOrderSteps.onlinePaymentCardSelection:
                        return 'md';
                    default:
                        break;
                }
                return 'md';
        }
    }, [step, paymentStep])

    const dialogActions = useMemo(() => {
        switch (step) {
            case steps.invoices:
                return {
                    show: true,
                    left: [{
                        onClick: () => close(),
                        text: "Close",
                    }],
                    right: [{
                        onClick: () => !allowApplyCredits
                            ? setStep(steps.summary)
                            : setStep(steps.credits),
                        text: "Next",
                    }]
                };
            case steps.credits:
                return {
                    show: true,
                    left: [{
                        onClick: () => setStep(steps.invoices),
                        text: "Back",
                    }],
                    right: [{
                        onClick: () => setStep(steps.summary),
                        text: "Next",
                    }]
                };
            case steps.summary:
                return {
                    show: true,
                    left: [{
                        onClick: () => !allowApplyCredits
                            ? setStep(steps.invoices)
                            : setStep(steps.credits),
                        text: "Back",
                    }],
                    right: [
                        {
                            onClick: () => setStep(steps.email),
                            text: "Send Email Confirmation",
                        },
                        ...(totalAmount > 0
                            ? [{
                                onClick: () => setStep(steps.payment),
                                text: "Add Payment",
                            }]
                            : []),
                    ]
                };
            case steps.email:
                return {
                    show: true,
                    left: [{
                        onClick: () => setStep(steps.summary),
                        text: "Back",
                        disabled: sendingEmail,
                    }],
                    right: [{
                        text: sendingEmail ? "Sending..." : 'Send Email',
                        onClick: () => sendEmailSubmitterRef.current?.click(),
                        disabled: sendingEmail,
                    }]
                }
            case steps.payment:
                switch (paymentStep) {
                    case PayCustomerOrderSteps.methodSelection:
                        return {
                            show: true,
                            left: [{
                                onClick: () => setStep(steps.summary),
                                text: "Back",
                            }],
                            right: []
                        }
                    case PayCustomerOrderSteps.onlinePaymentCardSelection:
                        return {
                            show: true,
                            left: [{
                                onClick: () => setPaymentStep(PayCustomerOrderSteps.methodSelection),
                                text: "Back",
                            }],
                            right: []
                        }
                    case PayCustomerOrderSteps.onlinePayment:
                        return {
                            show: true,
                            left: [{
                                onClick: () => setPaymentStep(PayCustomerOrderSteps.onlinePaymentCardSelection),
                                text: "Back",
                                disabled: submittingPaymentAction,
                            }],
                            right: [{
                                onClick: () => paymentRef.current?.executeAction?.(),
                                text: submittingPaymentAction ? "Paying..." : 'Proceed',
                                disabled: submittingPaymentAction,
                            }]
                        }
                    case PayCustomerOrderSteps.otherPayments:
                        return {
                            show: true,
                            left: [{
                                onClick: () => !operatorAccessLevels.packages.sales.onlinePayments ? setStep(steps.summary) : setPaymentStep(PayCustomerOrderSteps.methodSelection),
                                text: "Back",
                                disabled: submittingPaymentAction,
                            }],
                            right: [{
                                onClick: () => paymentRef.current?.executeAction?.(),
                                text: submittingPaymentAction ? "Paying..." : 'Proceed',
                                disabled: submittingPaymentAction,
                            }]
                        }
                    default:
                        return {
                            show: false,
                        }
                }
            default:
                return {
                    show: false,
                }
        }
    }, [step, totalAmount, sendingEmail, paymentStep, close, submittingPaymentAction, operatorAccessLevels.packages.sales.onlinePayments, allowApplyCredits])

    return (
        <Dialog
            disableBackdropClick
            open={open}
            setOpen={close}
            maxWidth={dialogSize}
            className={'bound-dialog pay-multi-invoices-dialog'}>
            <DialogTitle>
                <TopBarProgressIndicator
                    currentType={currentProcess}
                    types={visibleProcesses}
                    passed={passed}
                />
            </DialogTitle>
            <DialogContent className={'mt--3 pb-4'}>
                <div className='d-flex flex-wrap justify-content-center w-100'>
                    {
                        steps.invoices === step &&
                        <PayMultiInvoicesDialogInvoicesSection
                            selectedInvoices={selectedInvoices}
                        />
                    }
                    {
                        allowApplyCredits && steps.credits === step &&
                        <PayMultiInvoicesDialogCreditsSection
                            customerId={customerId}
                            selectedCredits={selectedCredits}
                            onAdd={addCreditToMultipleInvoices}
                            onRemove={removeCreditFromMultipleInvoices}
                        />
                    }
                    {
                        steps.summary === step &&
                        <PayMultipleInvoicesDialogSummarySection
                            customerId={customerId}
                            totalAmount={totalAmount}
                            selectedInvoices={selectedInvoices}
                            selectedCredits={selectedCredits}
                            appliedPayments={appliedPayments}
                            removeCredit={removeCreditFromMultipleInvoices}
                        />
                    }
                    {
                        steps.payment === step &&
                        <PayCustomerOrder
                            ref={paymentRef}
                            exit={() => setStep(steps.summary)}
                            pay={payMultipleInvoices}
                            step={paymentStep}
                            setStep={setPaymentStep}
                            customerId={customerId}
                            remainingBalance={totalAmount}
                            minChargeAmount={0}
                            setSubmittingAction={setSubmittingPaymentAction}
                            submittingAction={submittingPaymentAction}
                        />
                    }
                    {
                        steps.email === step &&
                        <PayCustomerOrderSendEmailSection
                            formId={sendEmailFormIdRef.current}
                            customerId={customerId}
                            state={{send: sendMultiInvoiceEmail}}
                            onEmailSent={() => setStep(steps.summary)}
                            setSubmitting={setSendingEmail}
                        />
                    }
                </div>
                {financialSummary}
            </DialogContent>
            <button
                ref={sendEmailSubmitterRef}
                className={'d-none'}
                form={sendEmailFormIdRef.current}
                type={'submit'}
            />
            {
                dialogActions.show &&
                <DialogActions className={'p-0 w-100 d-flex flex-column'}>
                    <Divider className='divider'/>
                    <div className={'d-flex flex-wrap w-100 justify-content-between align-items-center m-0'}>
                        <div className={'d-flex align-items-center justify-content-start flex-grow-1'}>
                            {
                                !!dialogActions.left.length &&
                                dialogActions.left.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className='px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`0 ${i === array.length - 1 ? '10px' : '0'} 0 ${i === 0 ? '10px' : '0'}`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                        <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                            {
                                !!dialogActions.right.length &&
                                dialogActions.right.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className=' px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`${i === 0 ? '10px' : '0'} 0 ${i === array.length - 1 ? '10px' : '0'} 0`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                    </div>
                </DialogActions>
            }
        </Dialog>
    );
}


export default PayMultiInvoicesDialog;
